import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputComponent } from 'src/app/components/shared/input';
import { DropdownComponent } from 'src/app/components/shared/dropdown';
import { CategoryService } from 'src/app/services/category';
import { LocaleService } from 'src/app/services/locale';
import { PlusComponent } from '../../../../icons/plus';
import { TrashComponent } from '../../../../icons/trash';

interface ElectricitySourceOption {
  value: string;
  label: string;
  italianLabel: string;
}

@Component({
  selector: 'app-category2-question2',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    DropdownComponent,
    PlusComponent,
    TrashComponent,
  ],
  template: `
    <!-- Header row with add button -->
    <div
      class="bg-[#043B22] flex px-3 py-2.5 rounded-t-lg gap-4 mt-2 items-center"
    >
      <div class="text-white text-[13px] flex-1">
        {{ strings[language].source_label }}
      </div>
      <div class="text-white text-[13px] flex-1">
        {{ strings[language].amount_label }}
      </div>
      <div
        (click)="addRow()"
        class="w-6 h-6 flex justify-center items-center cursor-pointer bg-white/20 hover:bg-white/30 rounded-full transition-colors flex-shrink-0"
      >
        <app-plus color="white"></app-plus>
      </div>
    </div>

    <form [formGroup]="form">
      <div formArrayName="entries">
        <div
          *ngFor="let entry of entries.controls; let i = index"
          [formGroupName]="i"
          class="flex py-3 px-1 border-b border-[#DEE2E6] gap-4 items-center"
        >
          <div class="flex-1">
            <app-dropdown
              [placeholder]="strings[language].electricitySource"
              [options]="electricitySourceDropdownOptions"
              [control]="getControl(i, 'electricitySource')"
              (change)="onChange()"
            ></app-dropdown>
          </div>
          <div class="flex-1">
            <app-input
              [keyUp]="onChange"
              type="number"
              [dollar]="false"
              unit="kWh"
              placeholder="0.00"
              [control]="getControl(i, 'ann')"
            ></app-input>
          </div>
          <div
            *ngIf="entries.length > 1"
            (click)="removeRow(i)"
            class="cursor-pointer flex-shrink-0"
          >
            <app-trash></app-trash>
          </div>
        </div>
      </div>
    </form>
  `,
})
export class Category2Question2Component implements OnInit {
  language = inject(LocaleService).getLanguage();
  categoryService = inject(CategoryService);
  categories = this.categoryService.categories();
  fb = inject(FormBuilder);

  electricitySourceOptions: ElectricitySourceOption[] = [
    {
      value: 'grid',
      label: 'Purchased from the National Grid',
      italianLabel: 'Acquistata dalla rete nazionale',
    },
    {
      value: 'renewable_supplier',
      label: 'Purchased from Renewable Supplier (PPA or Green Tariff)',
      italianLabel:
        'Acquistata tramite fornitore rinnovabile (PPA o tariffa verde)',
    },
    {
      value: 'self_generated',
      label: 'Self-generated On-Site (Solar, Wind, CHP, etc.)',
      italianLabel:
        'Autoprodotta da impianto on-site (solare, eolico, cogenerazione)',
    },
    {
      value: 'imported_other',
      label: 'Imported from Another Facility or Country',
      italianLabel: 'Importata da altra sede o paese',
    },
  ];

  strings: any = {
    english: {
      electricitySource: 'Select electricity source',
      source_label: 'Source',
      amount_label: 'Annual amount (kWh)',
    },
    italian: {
      electricitySource: "Seleziona la fonte dell'elettricità",
      source_label: 'Fonte',
      amount_label: 'Quantità annuale (kWh)',
    },
  };

  form!: FormGroup;

  get entries(): FormArray {
    return this.form.get('entries') as FormArray;
  }

  private _electricitySourceCache: { lang: string; opts: { value: string; label: string }[] } | null = null;

  get electricitySourceDropdownOptions() {
    const lang = this.language;
    if (this._electricitySourceCache?.lang === lang) {
      return this._electricitySourceCache.opts;
    }
    const opts = this.electricitySourceOptions.map((opt) => ({
      value: opt.value,
      label: lang === 'english' ? opt.label : opt.italianLabel,
    }));
    this._electricitySourceCache = { lang, opts };
    return opts;
  }

  ngOnInit() {
    this.form = this.fb.group({ entries: this.fb.array([]) });
    this.loadSavedData();
    if (this.entries.length === 0) {
      this.addRow();
    }
    this.form.valueChanges.subscribe(() => this.onChange());
  }

  addRow(entry?: { electricitySource: string; ann: string }) {
    this.entries.push(
      this.fb.group({
        electricitySource: [
          entry?.electricitySource || '',
          Validators.required,
        ],
        ann: [entry?.ann || '', Validators.required],
      }),
    );
  }

  removeRow(index: number) {
    this.entries.removeAt(index);
    this.onChange();
  }

  getControl(i: number, name: string): FormControl {
    return this.entries.at(i).get(name) as FormControl;
  }

  loadSavedData() {
    const saved = localStorage.getItem('categories');
    if (!saved) return;
    try {
      const categories = JSON.parse(saved);
      const answers = categories?.[1]?.questions?.[1]?.answers;
      if (!answers) return;

      if (Array.isArray(answers)) {
        // New array format
        answers.forEach((entry: any) => {
          this.addRow({
            electricitySource: entry.electricitySource || '',
            ann: String(entry.ann || ''),
          });
        });
      } else if (answers.electricitySource || answers.ann) {
        // Legacy single-object format — migrate to array
        this.addRow({
          electricitySource: answers.electricitySource || '',
          ann: String(answers.ann || ''),
        });
      }
    } catch (e) {
      console.error('Error loading saved data for cat2-q2', e);
    }
  }

  onChange = () => {
    const answers = this.entries.controls.map((c) => ({
      electricitySource: c.get('electricitySource')?.value || '',
      ann: c.get('ann')?.value || '',
    }));
    this.categories[1].questions[1].answers = answers as any;
    this.categoryService.persistCategories(this.categories);
  };
}
